import React, { useEffect, useMemo } from 'react'
import Typography, { TypographyColor } from '../../components/typography'
import Box from '../../components/box'
import Link from '../../components/router/link'
import GroupIcon from '@mui/icons-material/Group';
import { Stack } from '@mui/material'
import DurationIcon from '@mui/icons-material/WatchLater';
import LocationIcon from '@mui/icons-material/LocationOn';
import useCurrentUserId from '../../hooks/useCurrentUserId';
import Button from '../../components/button';
import WarningIcon from '@mui/icons-material/Warning';
import { StudioService } from '../../apilibrary';
import { StudioPendingAction } from '../../apilibrary/types/studio';

export interface OwnerDetailProps {
  ownerId: string
  firstName?: string
  lastName?: string
}

interface StudioHeaderFragmentProps {
  studioId: string
  title: string
  city: string
  state: string
  maxOccupancy: number
  minSessionLength: number
  ownerProfile: OwnerDetailProps
}

const StudioHeaderFragment: React.FC<StudioHeaderFragmentProps> = ({
  studioId,
  title,
  ownerProfile,
  minSessionLength,
  city,
  state,
  maxOccupancy
}) => {
  const [pendingActions, setPendingActions] = React.useState<string[]>([])
  const currentUser = useCurrentUserId()

  useEffect(() => {
    const fetchPendingActions = async (): Promise<void> => {
      const pendingActions = await StudioService.getStudioPendingActions(studioId)
      setPendingActions(pendingActions)
    }
    if (currentUser === ownerProfile.ownerId) {
      void fetchPendingActions()
    }
  }, [currentUser, ownerProfile.ownerId, studioId])

  const warningText = useMemo(() => {
    if (pendingActions.includes(StudioPendingAction.STRIPE_ACCOUNT_ONBOARDING)) {
      return 'Setup Stripe Account to begin publically accepting bookings'
    }
    return undefined
  }, [pendingActions])

  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-end'}>
        <Typography display={'block'} fontWeight={'bold'} variant='h1'>
          {title}
        </Typography>
        {currentUser === ownerProfile.ownerId && (
          <Box>
            <Link to={`/studio/${studioId}/edit`}>
              <Button color='primary' size='small' variant='outlined'>
                Edit Studio
              </Button>
            </Link>
          </Box>
        )}
      </Stack>
      {warningText && (
        <Box mt={2}>
          <Link to={'/settings/profile'}>
            <Typography leftIcon={
              <WarningIcon sx={{ color: TypographyColor.WARNING }} />
            } display={'block'} color={TypographyColor.WARNING} fontWeight={'bold'} fontSize={'18px'}>
              {warningText}
            </Typography>
          </Link>
        </Box>
      )}
      <Stack mt={2} spacing={{ xs: 2, sm: 3, md: 5 }} direction="row" useFlexGap flexWrap="wrap">
        <Box>
          <Typography display={'inline-block'} fontSize={'16px'}>Provided by &nbsp;</Typography>
          <Link to={`/profile/${ownerProfile.ownerId}`}>
            <Typography display={'inline-block'} fontSize={'16px'} fontWeight={500}>
              {ownerProfile.firstName ?? 'NoName'} {ownerProfile.lastName ?? 'User'}
            </Typography>
          </Link>
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <LocationIcon sx={{ color: 'white' }} />
          <Typography display={'block'} fontSize={'16px'}>
            {city}, {state}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <GroupIcon sx={{ color: 'white' }} />
          <Typography display={'block'} fontSize={'16px'}>
            {maxOccupancy} people max
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <DurationIcon sx={{ color: 'white' }} />
          <Typography display={'block'} fontSize={'16px'}>
            {minSessionLength} minutes minimum
          </Typography>
        </Stack>
      </Stack>
    </>
  )
}

export default StudioHeaderFragment
